import InventoryStore, { CreateInventoryItemParams } from 'admin/InventoryStore'
import { task } from 'mobx-task'
import Workspace from 'iam/models/Workspace'
import { action, observable, makeObservable, override } from 'mobx'
import WorkspaceSelectorViewModel from 'admin/inventory/WorkspaceSelectorViewModel'
import { validationContext } from 'validx'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { BaseInventoryItemViewModel } from '../BaseInventoryItemDialog/BaseInventoryItemViewModel'
import { IWithWorkspaceSelection } from '../../components/InventoryItemFormWorkpaceDropdown'
import { RefreshInventoryItems } from 'admin/inventory/InventorySearchViewModel'
import { SupportingItemsPorts } from '../../../ports'

export default class CreateWorkspaceInventoryItemViewModel
  extends BaseInventoryItemViewModel
  implements IWithWorkspaceSelection
{
  /**
   * The selected workspace
   */
  @observable workspace? = Workspace.none

  /**
   * Validations for the workspace
   */
  workspaceValidation = validationContext(this, {
    workspace: [(opts) => !!opts.value || 'Workspace is required'],
  })

  constructor(
    private inventoryStore: InventoryStore,
    private refreshItems: RefreshInventoryItems,
    public getSelectedWorkspace: () => Workspace | undefined,
    public workspaceSelectorVM: WorkspaceSelectorViewModel,
    private newAlert: AlertFactory,
    supportingItemsAdapter: SupportingItemsPorts,
  ) {
    super(supportingItemsAdapter)
    makeObservable(this)
  }

  /**
   * Shows the dialog
   */
  @override
  show() {
    // Have to use override because of mobx 6 strict mode in subclasses.
    this.workspace = this.getSelectedWorkspace()
    super.show()
  }

  /**
   * Set the workspace for the new item
   */
  @action.bound
  setWorkspace(workspace?: Workspace) {
    this.workspace = workspace
  }

  @override
  open() {
    this.reset()
    this.show()
  }

  /**
   * Creates the inventory item
   */
  submit = task.resolved(async () => {
    if (
      !this.validate() ||
      !this.workspaceValidation.reset().validate().isValid
    ) {
      return
    }

    const typeSpec = this.getInventoryItemTypeSpecification()

    const pricing = this.pricingViewModel.toValues()

    const payload: CreateInventoryItemParams = {
      name: this.name,
      description: this.description,
      courtesyAmount: this.courtesyQuantity,
      pricing,
      workspace: this.workspace?.id,
      supportingItems: this.supportingItems,
      hideFromPro: this.hideFromPro,
      typeSpecification: typeSpec,
    }

    await this.inventoryStore
      .create(payload)
      .then(async () => {
        await this.inventoryStore.fetchWorkspacesWithInventoryItems()

        // Refresh the list to show new items
        await this.refreshItems(this.workspace || Workspace.none)

        this.validation.reset()

        this.newAlert()
          .withState(AlertState.Success)
          .saying('Success', 'Item added.')
          .autoDismiss()
          .show()

        this.close()
      })
      .catch((e) => {
        this.newAlert()
          .withState(AlertState.Error)
          .saying('Error', e.message)
          .autoDismiss(2000)
          .show()
      })
  })
}
